import { useIsOnline } from "@/hooks/useIsOnline";
import { useMobileHeaderOptions } from "@/hooks/useMobileHeader";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { SignalLowIcon, WifiIcon, WifiOffIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

/** How long "back online" stays up once the network returns. */
const RECONNECTED_NOTICE_MS = 2500;

type ConnectionNotice = "offline" | "reconnected" | "slow";

/**
 * The network, said under the header the moment it matters, as a native app
 * does: lost (what is open stays readable, the rest resumes by itself), back,
 * or slow on what the screen is waiting for. Nothing the rest of the time — a
 * phone on a good network never sees it.
 */
export function MobileConnectionBanner() {
  const { t } = useTranslation();
  const isOnline = useIsOnline();
  const { isConnectionSlow } = useMobileHeaderOptions();
  const [isReconnected, setIsReconnected] = useState(false);
  const wasOfflineRef = useRef(!isOnline);

  useEffect(() => {
    if (!isOnline) {
      wasOfflineRef.current = true;
      setIsReconnected(false);
      return;
    }
    if (!wasOfflineRef.current) return;
    wasOfflineRef.current = false;
    setIsReconnected(true);
    const timeout = window.setTimeout(
      () => setIsReconnected(false),
      RECONNECTED_NOTICE_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [isOnline]);

  const notice: ConnectionNotice | null = !isOnline
    ? "offline"
    : isReconnected
      ? "reconnected"
      : isConnectionSlow
        ? "slow"
        : null;

  const Icon =
    notice === "offline"
      ? WifiOffIcon
      : notice === "reconnected"
        ? WifiIcon
        : SignalLowIcon;

  return (
    // Always in the tree, so a screen reader hears each change of the region.
    <div role="status" aria-live="polite" className="shrink-0">
      {notice && (
        <div
          className={cn(
            "flex gap-2 items-start px-4 py-2 text-sm animate-in fade-in slide-in-from-top-1",
            notice === "offline"
              ? "bg-foreground text-background"
              : "bg-muted text-muted-foreground",
          )}
        >
          <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
          <p>
            <span className="font-medium">
              {t(`components.mobileShell.connection.${notice}.title`)}
            </span>
            {notice !== "reconnected" && (
              <span className="opacity-80">
                {" "}
                {t(`components.mobileShell.connection.${notice}.description`)}
              </span>
            )}
          </p>
        </div>
      )}
    </div>
  );
}
